/**
 * Pure H OS · Unified Approvals Inbox (Wave A · Stabilization Sprint).
 *
 * Aggregates every "needs owner call" item across the platform:
 *   • AI approvals (ai_approvals, pending)
 *   • Purchase Orders (procurement, draft / pending)
 *   • Vendor payments (vendor_payments, submitted / hold)
 *   • Expenses (expenses, submitted / scheduled)
 *   • Invoices (invoices, draft / sent)
 *   • Decisions (decisions, hold / deferred)
 *   • R&D Decisions (rnd_decisions, hold)
 *
 * Queue-only page. Every row deep-links to the source module's drawer.
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, SectionHeader, SeverityBadge, TextInput } from "../components/lib";
import { ListRow, TabBar, Field, Drawer, Segmented } from "../components/lib.overlay";
import { AuditTrail } from "../components/AuditTrail";
import { AI_APPROVALS } from "../mocks/aiData";
import { PROCUREMENT } from "../mocks/procurementData";
import { DECISIONS }   from "../mocks/decisionsData";
import { RND_DECISIONS } from "../mocks/rndData";
import { INVOICES, EXPENSES, VENDOR_PAYMENTS } from "../mocks/financeData";

type Kind = "AI" | "PO" | "Expense" | "Invoice" | "VendorPayment" | "Decision" | "R&D Decision";

type Row = {
  key: string;
  id: string;
  kind: Kind;
  title: string;
  detail: string;
  amountEGP?: number;
  severity: "low" | "medium" | "high";
  dueRelative?: string;
  requester: string;
  approver: string;
  chain: string[];
  collection: string;
  href: string;
};

type Tab = "all" | Kind;
type PanelTab = "overview" | "chain" | "comments" | "attachments" | "linked" | "audit";

const CHAIN = ["Requester", "Department Manager", "Finance", "Executive", "Owner"];

export default function UnifiedApprovals() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState<Row | null>(null);
  const [wsTab, setWsTab] = useState<PanelTab>("overview");

  const rows: Row[] = useMemo(() => {
    const aiRows: Row[] = AI_APPROVALS
      .filter((a) => a.status === "pending")
      .map((a) => ({
        key: `ai-${a.id}`, id: a.id, kind: "AI",
        title: a.title,
        detail: "Awaiting decision",
        amountEGP: a.amountEGP,
        severity: "medium" as Row["severity"],
        requester: a.askedBy,
        approver: "Executive",
        chain: CHAIN,
        collection: "ai_approvals",
        href: "/pureh/ai/approvals",
      }));

    const poRows: Row[] = PROCUREMENT
      .filter((p) => p.status === "pending" || p.status === "draft")
      .map((p) => ({
        key: `po-${p.id}`, id: p.id, kind: "PO",
        title: `${p.poNumber} · ${p.supplier}`,
        detail: `${p.category} · ordered ${p.orderedRelative}`,
        amountEGP: p.amountEGP,
        severity: (p.status === "pending" ? "medium" : "low") as Row["severity"],
        dueRelative: p.expectedRelative,
        requester: p.owner || "Procurement",
        approver: "Finance",
        chain: CHAIN,
        collection: "procurement",
        href: `/pureh/procurement?open=${p.id}`,
      }));

    const expRows: Row[] = EXPENSES
      .filter((e) => (e.status as string) === "submitted" || (e.status as string) === "scheduled")
      .map((e) => ({
        key: `exp-${e.id}`, id: e.id, kind: "Expense",
        title: `${e.vendor} · ${e.description}`,
        detail: `${e.category} · ${e.paidRelative || (e as unknown as { dateRelative?: string }).dateRelative || ""}`,
        amountEGP: e.amountEGP,
        severity: ((e.status as string) === "submitted" ? "medium" : "low") as Row["severity"],
        requester: (e as unknown as { owner?: string }).owner || "Finance",
        approver: "Finance",
        chain: CHAIN,
        collection: "expenses",
        href: `/pureh/finance/expenses?open=${e.id}`,
      }));

    const vpRows: Row[] = VENDOR_PAYMENTS
      .filter((v) => v.status === "queued" || v.status === "on-hold")
      .map((v) => ({
        key: `vp-${v.id}`, id: v.id, kind: "VendorPayment",
        title: `${v.vendor} · ${v.reference}`,
        detail: `${v.method} · due ${v.dueRelative}`,
        amountEGP: v.amountEGP,
        severity: (v.status === "on-hold" ? "high" : "medium") as Row["severity"],
        dueRelative: v.dueRelative,
        requester: v.approvedBy || "AP",
        approver: "Finance",
        chain: CHAIN,
        collection: "vendor_payments",
        href: `/pureh/finance/vendor-payments?open=${v.id}`,
      }));

    const invRows: Row[] = INVOICES
      .filter((i) => i.status === "draft" || i.status === "sent")
      .map((i) => ({
        key: `inv-${i.id}`, id: i.id, kind: "Invoice",
        title: `${i.number} · ${i.customer}`,
        detail: `${i.status} · due ${i.dueRelative}`,
        amountEGP: i.amountEGP,
        severity: (i.status === "draft" ? "medium" : "low") as Row["severity"],
        dueRelative: i.dueRelative,
        requester: "Sales",
        approver: "Finance",
        chain: CHAIN,
        collection: "invoices",
        href: `/pureh/finance/invoices?open=${i.id}`,
      }));

    const decRows: Row[] = DECISIONS
      .filter((d) => d.outcome === "hold" || d.outcome === "deferred")
      .map((d) => ({
        key: `dec-${d.id}`, id: d.id, kind: "Decision",
        title: d.title,
        detail: `${d.module} · ${d.dateRelative}`,
        amountEGP: typeof d.budgetImpactEGP === "number" ? d.budgetImpactEGP : undefined,
        severity: d.severity,
        requester: d.decidedBy,
        approver: "Owner",
        chain: CHAIN,
        collection: "decisions",
        href: `/pureh/decisions?open=${d.id}`,
      }));

    const rndRows: Row[] = RND_DECISIONS
      .filter((d) => d.decision === "hold")
      .map((d) => ({
        key: `rd-${d.id}`, id: d.id, kind: "R&D Decision",
        title: `Hold · ${d.productId}`,
        detail: `${d.rationale} · ${d.dateRelative}`,
        amountEGP: d.budgetImpactEGP,
        severity: "medium",
        requester: d.decidedBy,
        approver: "Owner",
        chain: CHAIN,
        collection: "rnd_decisions",
        href: `/pureh/rd/decisions`,
      }));

    return [...aiRows, ...poRows, ...expRows, ...vpRows, ...invRows, ...decRows, ...rndRows];
  }, []);

  const filtered = useMemo(() => rows.filter((r) =>
    (tab === "all" || r.kind === tab) &&
    (!q || r.title.toLowerCase().includes(q.toLowerCase()) || r.detail.toLowerCase().includes(q.toLowerCase()) || r.requester.toLowerCase().includes(q.toLowerCase())),
  ), [rows, tab, q]);

  const totalAmount = rows.filter((r) => typeof r.amountEGP === "number").reduce((n, r) => n + (r.amountEGP || 0), 0);
  const highSev = rows.filter((r) => r.severity === "high").length;

  return (
    <div className="space-y-6" data-testid="approvals-page">
      {/* ── Summary ─────────────────────────────────────────────── */}
      <Card testId="apv-summary">
        <SectionHeader eyebrow="Global · Approvals Inbox" title={`${rows.length} item${rows.length === 1 ? "" : "s"} awaiting decision`} />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Total items</div><div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-ink-strong)" }}>{rows.length}</div></div>
          <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>High severity</div><div className="text-xl font-semibold tabular-nums mt-1" style={{ color: highSev > 0 ? "var(--ph-danger)" : "var(--ph-success)" }}>{highSev}</div></div>
          <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Total value</div><div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-ink-strong)" }}>EGP {(totalAmount / 1000).toFixed(0)}K</div></div>
          <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Distinct channels</div><div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-brand-primary)" }}>{new Set(rows.map((r) => r.kind)).size}</div></div>
        </div>
      </Card>

      {/* ── Filters ─────────────────────────────────────────────── */}
      <Card testId="apv-filters">
        <TextInput value={q} onChange={setQ} placeholder="Search title / detail / requester…" testId="apv-search" />
        <div className="mt-4 overflow-x-auto">
          <TabBar<Tab>
            testId="apv-tabs"
            active={tab}
            onChange={setTab}
            tabs={[
              { key: "all",           label: "All",           count: rows.length },
              { key: "AI",            label: "AI",            count: rows.filter((r) => r.kind === "AI").length },
              { key: "PO",            label: "PO / PR",       count: rows.filter((r) => r.kind === "PO").length },
              { key: "Expense",       label: "Expense",       count: rows.filter((r) => r.kind === "Expense").length },
              { key: "Invoice",       label: "Invoice",       count: rows.filter((r) => r.kind === "Invoice").length },
              { key: "VendorPayment", label: "Vendor Payment",count: rows.filter((r) => r.kind === "VendorPayment").length },
              { key: "Decision",      label: "Decision",      count: rows.filter((r) => r.kind === "Decision").length },
              { key: "R&D Decision",  label: "R&D Decision",  count: rows.filter((r) => r.kind === "R&D Decision").length },
            ]}
          />
        </div>
      </Card>

      {/* ── Queue ───────────────────────────────────────────────── */}
      <Card padded={false} testId="apv-list">
        <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <SectionHeader eyebrow="Queue · click a row to inspect · double-click to open source" title={`${filtered.length} item${filtered.length === 1 ? "" : "s"}`} />
          <div className="text-xs" style={{ color: "var(--ph-muted)" }}>
            One queue across Finance · Operations · Procurement · Marketing · R&amp;D · Executive · Launch · Growth · HR (future ready).
          </div>
        </div>
        <div className="p-2">
          {filtered.length === 0 ? (
            <div className="p-5 text-sm" style={{ color: "var(--ph-muted)" }}>Nothing to approve right now.</div>
          ) : filtered.map((r) => (
            <ListRow key={r.key} testId={`apv-${r.key}`}
              onClick={() => { setSelected(r); setWsTab("overview"); }}
              label={
                <span className="flex items-center gap-2">
                  <span className="uppercase tracking-[0.14em] text-[10px]" style={{ color: "var(--ph-brand-primary)" }}>{r.kind}</span>
                  <span>{r.title}</span>
                </span>
              }
              sublabel={<>Requester: {r.requester} · Approver: {r.approver} · {r.detail}{r.dueRelative ? ` · due ${r.dueRelative}` : ""}</>}
              trailing={
                <div className="text-right">
                  {typeof r.amountEGP === "number" && r.amountEGP !== 0 && (
                    <div className="text-sm tabular-nums font-semibold" style={{ color: "var(--ph-ink-strong)" }}>EGP {Math.abs(r.amountEGP).toLocaleString()}</div>
                  )}
                  <SeverityBadge severity={r.severity} />
                </div>
              }
            />
          ))}
        </div>
      </Card>

      {/* ── Inspection drawer ───────────────────────────────────── */}
      <Drawer open={Boolean(selected)} onClose={() => setSelected(null)} title="Approval 360" testId="apv-drawer" width={720}>
        {selected && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-brand-primary)" }}>{selected.kind}</div>
            <div style={{ fontFamily: "var(--ph-display-font)", fontSize: "1.35rem", color: "var(--ph-ink-strong)" }} className="mt-1 mb-3">{selected.title}</div>

            <div className="mb-4 overflow-x-auto">
              <Segmented<PanelTab>
                testId="apv-ws-tabs"
                value={wsTab}
                onChange={setWsTab}
                options={[
                  { value: "overview",    label: "Overview"    },
                  { value: "chain",       label: "Chain"       },
                  { value: "comments",    label: "Comments"    },
                  { value: "attachments", label: "Attachments" },
                  { value: "linked",      label: "Linked"      },
                  { value: "audit",       label: "Audit"       },
                ]}
              />
            </div>

            {wsTab === "overview" && (
              <div data-testid="apv-ws-overview">
                <Field label="Kind"      inline><span className="text-sm font-semibold">{selected.kind}</span></Field>
                <Field label="Detail"><span className="text-sm">{selected.detail}</span></Field>
                <Field label="Requester" inline><span className="text-sm">{selected.requester}</span></Field>
                <Field label="Approver"  inline><span className="text-sm">{selected.approver}</span></Field>
                {selected.dueRelative && <Field label="Due" inline><span className="text-sm">{selected.dueRelative}</span></Field>}
                {typeof selected.amountEGP === "number" && <Field label="Amount" inline><span className="text-sm tabular-nums font-semibold">EGP {Math.abs(selected.amountEGP).toLocaleString()}</span></Field>}
                <Field label="Severity"  inline><SeverityBadge severity={selected.severity} /></Field>
              </div>
            )}

            {wsTab === "chain" && (
              <div data-testid="apv-ws-chain">
                <div className="text-[10px] uppercase tracking-[0.14em] mb-2" style={{ color: "var(--ph-ink-muted)" }}>Approval chain</div>
                {selected.chain.map((step, i) => (
                  <ListRow key={step} testId={`apv-chain-${i}`}
                    label={step}
                    sublabel={step === "Requester" ? selected.requester : step === "Owner" || step === "Executive" ? "Pending decision" : "Reviewed"}
                  />
                ))}
              </div>
            )}

            {wsTab === "comments" && <div data-testid="apv-ws-comments"><div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>Discussion threads open at audit level.</div></div>}
            {wsTab === "attachments" && <div data-testid="apv-ws-attachments"><div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>Attachments live on the source object drawer.</div></div>}
            {wsTab === "linked" && (
              <div data-testid="apv-ws-linked">
                <Field label="Source module"     inline><span className="text-sm">{selected.kind}</span></Field>
                <Field label="Source collection" inline><span className="text-sm font-mono">{selected.collection}</span></Field>
                <Field label="Deep link"><span className="text-sm font-mono">{selected.href}</span></Field>
                <div className="pt-3">
                  <button onClick={() => navigate(selected.href)} className="text-sm" style={{ color: "var(--ph-brand-primary)", textDecoration: "underline" }} data-testid="apv-open-source">Open source drawer →</button>
                </div>
              </div>
            )}
            {wsTab === "audit" && (
              <div data-testid="apv-ws-audit">
                <AuditTrail collection={selected.collection} recordId={selected.id} testId="apv-audit" />
              </div>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
}
